import { useEffect, useRef, useState } from "react";
import { useCallList } from "@/services/call.query";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { agentPickerSource } from "@/services/assistant.query";
import { usePhoneNumbers } from "@/hooks/use-phone-numbers";
import { useCampaigns } from "@/services/campaign.query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CallDetailsDialog } from "@/components/CallHistory/CallDetailsDialog";
import { CustomFilter } from "@/components/CustomFilter";
import { LazyChecklist } from "@/components/ui/lazy-checklist";
import { callHistoryFilterConfig, FilterConfig } from "@/config/callHistoryFilters";
import {
  Info, PhoneIncoming, PhoneOutgoing, Monitor,
  ChevronLeft, ChevronRight, Loader2,
  X, SlidersHorizontal, RefreshCw, Download,
} from "lucide-react";
import {
  CallLog,
  fetchAllCallLogs,
  ExportCancelledError,
  ExportProgress,
} from "@/services/call.service";
import { format } from "date-fns";
import { formatDisconnectionReason, getDisconnectionReasonInfo } from "@/utils/disconnection-reasons";
import { formatCallDuration } from "@/utils/call-duration";
import {
  agentLabel,
  exportCalls,
  exportRowLimit,
  CallExportFormat,
  DEFAULT_CALL_EXPORT_FIELD_IDS,
} from "@/utils/call-export";
import { orNA } from "@/utils/placeholder";
import { ExportDialog } from "@/components/CallHistory/ExportDialog";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";

export default function CallHistory() {
  const { currentWorkspaceId } = useWorkspace();
  const { data: phoneNumbers } = usePhoneNumbers(currentWorkspaceId || undefined);
  const { data: campaigns } = useCampaigns(currentWorkspaceId || undefined);
  
  const { toast } = useToast();
  const [selectedCall, setSelectedCall] = useState<CallLog | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Kept on the page so a picked column set survives closing the dialog.
  const [exportFields, setExportFields] = useState<string[]>(DEFAULT_CALL_EXPORT_FIELD_IDS);
  const [exportFormat, setExportFormat] = useState<CallExportFormat>("xlsx");
  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(null);
  // Lets the dialog's Cancel button stop a long fetch instead of just hiding it.
  const exportAbort = useRef<AbortController | null>(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Filters
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  // The Agent filter pages the agent list instead of downloading all of it, so
  // an agent's name is kept from the page it was ticked on — the chip above the
  // table shows it. Each row's own agent name comes joined onto the row.
  const [agentNames, setAgentNames] = useState<Record<string, string>>({});

  // Shared by the table query and the export, so a download always matches
  // exactly what the filters are showing.
  const callFilters = {
    callTypes: filters.callType?.length ? filters.callType : undefined,
    agentIds: filters.agent?.length ? filters.agent : undefined,
    campaignIds: filters.campaign?.length ? filters.campaign : undefined,
    timeRanges: filters.timeRange?.length ? filters.timeRange : undefined,
    fromNumbers: filters.from?.length ? filters.from : undefined,
    toSearch: filters.to?.[0] || undefined,
    callIdSearch: filters.callId?.[0] || undefined,
    durationRanges: filters.duration?.length ? filters.duration : undefined,
    costRanges: filters.cost?.length ? filters.cost : undefined,
    disconnectionReasons: filters.disconnectionReason?.length ? filters.disconnectionReason : undefined,
  };

  const { data: callLogsData, isLoading, refetch, isFetching } =
    useCallList(currentWorkspaceId, page, pageSize, callFilters);

  const callLogs = callLogsData?.data || [];
  const totalPages = callLogsData?.totalPages || 0;
  const totalCount = callLogsData?.count || 0;

  useEffect(() => {
    if (selectedCall && callLogs) {
      const updated = callLogs.find((c) => c.id === selectedCall.id);
      if (updated && updated.updated_at !== selectedCall.updated_at) {
        setSelectedCall(updated);
      }
    }
  }, [callLogs, selectedCall]);

  const hasActiveFilters = Object.values(filters).some((v) => v.length > 0);

  const clearFilters = () => {
    setFilters({});
    setPage(1);
  };

  const calculateCost = (call: CallLog) => {
    // Use actual cost from database if available (stored in cents)
    if (call.cost !== null && call.cost !== undefined) {
      const costInDollars = call.cost / 100;
      return `$${costInDollars.toFixed(2)}`;
    }
    
    // If cost is null/undefined, the call was not charged
    return "$0.00";
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "default";
      case "in-progress":
        return "secondary";
      case "failed":
        return "destructive";
      default:
        return "outline";
    }
  };

  const handleMoreInfo = (call: CallLog) => {
    setSelectedCall(call);
    setDetailsOpen(true);
  };

  const handleExport = async () => {
    if (!currentWorkspaceId) return;
    const controller = new AbortController();
    exportAbort.current = controller;
    const maxRows = exportRowLimit(exportFields);
    setExportProgress(null);
    setIsExporting(true);
    try {
      // Every row the current filters match, not just the page on screen.
      const rows = await fetchAllCallLogs(currentWorkspaceId, callFilters, {
        maxRows,
        signal: controller.signal,
        onProgress: setExportProgress,
      });
      const { clipped } = await exportCalls(
        rows,
        exportFields,
        {
          workspaceId: currentWorkspaceId,
          format: exportFormat,
          baseFileName: `call-history-${format(new Date(), "yyyy-MM-dd")}`,
          signal: controller.signal,
          onProgress: setExportProgress,
        },
      );
      setExportOpen(false);
      // Both of these change what the file actually contains, so they belong in
      // front of the user rather than only inside the cells.
      const notes = [
        rows.length >= maxRows
          ? `Capped at the first ${maxRows.toLocaleString()} — narrow the filters to export the rest.`
          : null,
        clipped > 0
          ? `${clipped.toLocaleString()} ${clipped === 1 ? "cell was" : "cells were"} too long for Excel and end in "… [truncated]" — export as CSV for the full text.`
          : null,
      ].filter(Boolean);
      toast({
        title: `Exported ${rows.length.toLocaleString()} ${rows.length === 1 ? "call" : "calls"}`,
        description: notes.length > 0 ? notes.join(" ") : undefined,
      });
    } catch (error) {
      if (error instanceof ExportCancelledError) {
        toast({ title: "Export cancelled" });
      } else {
        toast({
          variant: "destructive",
          title: "Export failed",
          description: error instanceof Error ? error.message : "Please try again.",
        });
      }
    } finally {
      exportAbort.current = null;
      setExportProgress(null);
      setIsExporting(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleFilterChange = (key: string, values: string[]) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: values }));
  };

  const filterConfig: FilterConfig[] = callHistoryFilterConfig.map((f) => {
    if (f.key === "agent")
      return {
        ...f,
        formatChipLabel: (id) => orNA(agentNames[id], "Agent"),
        render: (selected, setSelected) => (
          <LazyChecklist
            {...agentPickerSource(currentWorkspaceId || undefined)}
            enabled={!!currentWorkspaceId}
            value={selected.map((id) => ({ id, name: orNA(agentNames[id]) }))}
            onChange={(picked) => {
              setAgentNames((known) => ({
                ...known,
                ...Object.fromEntries(picked.map((agent) => [agent.id, agent.name])),
              }));
              setSelected(picked.map((agent) => agent.id));
            }}
            searchPlaceholder="Search agents…"
            emptyMessage="No agents found."
          />
        ),
      };
    if (f.key === "campaign")
      return { ...f, options: campaigns?.map((c) => ({ label: c.campaign_name, value: c.id })) ?? [] };
    if (f.key === "from")
      return { ...f, options: phoneNumbers?.map((p) => ({ label: p.nickname || p.phone_number, value: p.phone_number })) ?? [] };
    return f;
  });

  const getCallTypeIcon = (callType: string) => {
    switch (callType) {
      case 'inbound':
        return <PhoneIncoming className="w-4 h-4" />;
      case 'outbound':
        return <PhoneOutgoing className="w-4 h-4" />;
      default:
        return <Monitor className="w-4 h-4" />;
    }
  };

  // Mobile Card Component
  return (
    <>
    <div className="flex flex-col h-full min-h-0 bg-gradient-to-br from-background to-muted/20 overflow-hidden">
      {/* Header Section */}
      <div className="flex-shrink-0 p-6 pb-4 space-y-4 border-b bg-background/80 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Call History</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {totalCount > 0 ? `${totalCount.toLocaleString()} ${totalCount === 1 ? 'call' : 'calls'} total` : 'View and analyze your call records'}
            </p>
          </div>
          
        </div>

        {/* Filter + Refresh */}
        <div className="flex items-center justify-end gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-9 text-foreground"
              onClick={() => setExportOpen(true)}
              disabled={totalCount === 0}
              aria-label="Export calls"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-9 text-foreground"
              onClick={() => setFilterDrawerOpen(true)}
              aria-label="Filters"
            >
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              Filters
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-foreground"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            </Button>
        </div>

        {/* Active Filter Chips */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2">
            {filterConfig.flatMap((f) =>
              filters[f.key].map((val) => {
                const label = f.formatChipLabel ? f.formatChipLabel(val) : ((f.options ?? []).find((o) => o.value === val)?.label ?? val);
                return (
                  <span
                    key={`${f.key}-${val}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20"
                  >
                    {label}
                    <button
                      onClick={() => handleFilterChange(f.key, filters[f.key].filter((v) => v !== val))}
                      className="ml-0.5 hover:text-primary/60"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })
            )}
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-xs text-muted-foreground">
              Clear all
            </Button>
          </div>
        )}
      </div>

      {/* Scrollable Content with Footer */}
      <ScrollArea className="flex-1 min-h-0 overflow-hidden">
        <div className="p-6 max-w-full overflow-hidden">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                <Loader2 className="w-8 h-8 animate-spin mb-4" />
                <p>Loading call history...</p>
              </div>
            ) : callLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <PhoneIncoming className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium mb-1">No calls found</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  {hasActiveFilters
                    ? "Try adjusting your filters to see more results"
                    : "Your call history will appear here once you start making calls"}
                </p>
              </div>
            ) : (
              <>
                <div className="rounded-xl border bg-card shadow-sm overflow-hidden w-full max-w-full">
                  <div className="overflow-x-auto max-w-full">
                    <Table className="w-full table-fixed">
                      <TableHeader>
                        <TableRow className="bg-muted/50 hover:bg-muted/50">
                          <TableHead className="font-semibold w-[140px]">From</TableHead>
                          <TableHead className="font-semibold w-[180px]">To</TableHead>
                          <TableHead className="font-semibold w-[140px]">Agent</TableHead>
                          <TableHead className="font-semibold w-[90px]">Type</TableHead>
                          <TableHead className="font-semibold w-[80px]">Duration</TableHead>
                          <TableHead className="font-semibold w-[70px]">Cost</TableHead>
                          <TableHead className="font-semibold w-[90px]">Status</TableHead>
                          <TableHead className="font-semibold w-[110px]">Reason</TableHead>
                          <TableHead className="font-semibold w-[90px]">Date</TableHead>
                          <TableHead className="text-right font-semibold w-[70px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {callLogs.map((call) => (
                          <TableRow 
                            key={call.id} 
                            className="group hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={() => handleMoreInfo(call)}
                          >
                            <TableCell className="font-medium w-[140px] overflow-hidden">
                              <div className="truncate" title={call.from_number}>
                                {call.from_number}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground w-[180px] overflow-hidden">
                              <div className="truncate" title={call.to_number}>
                                {call.to_number}
                              </div>
                            </TableCell>
                            <TableCell className="font-medium w-[140px] overflow-hidden">
                              <div className="truncate" title={agentLabel(call)}>
                                {agentLabel(call)}
                              </div>
                            </TableCell>
                            <TableCell className="w-[90px] overflow-hidden">
                              <Badge variant="outline" className="gap-1.5 font-medium text-xs">
                                {getCallTypeIcon(call.call_type)}
                                <span className="capitalize truncate">{call.call_type}</span>
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono w-[80px]">{formatCallDuration(call)}</TableCell>
                            <TableCell className="w-[70px]">{calculateCost(call)}</TableCell>
                            <TableCell className="w-[90px] overflow-hidden">
                              <Badge variant={getStatusColor(call.status)} className="capitalize text-xs truncate">
                                {call.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="w-[110px] overflow-hidden">
                              {call.disconnection_reason ? (
                                <Badge 
                                  variant={
                                    getDisconnectionReasonInfo(call.disconnection_reason)?.callStatus === 'error' 
                                      ? 'destructive' 
                                      : getDisconnectionReasonInfo(call.disconnection_reason)?.callStatus === 'not_connected'
                                      ? 'secondary'
                                      : 'outline'
                                  }
                                  className="text-xs truncate"
                                >
                                  {formatDisconnectionReason(call.disconnection_reason)}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground w-[90px]">
                              <div className="flex flex-col">
                                <span className="font-medium text-foreground text-xs">{format(new Date(call.created_at), 'MMM dd')}</span>
                                <span className="text-xs">{format(new Date(call.created_at), 'HH:mm')}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right w-[70px]">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleMoreInfo(call); }}
                              >
                                <Info className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            )}

          {/* Pagination Footer - At bottom of scrollable content */}
          {totalCount > 0 && (
            <div className="flex-shrink-0 p-4 border-t bg-background/80 backdrop-blur-sm mt-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalCount)} of {totalCount}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1 || isLoading}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    <span>Previous</span>
                  </Button>
                  <span className="text-sm font-medium px-2">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages || isLoading}
                  >
                    <span>Next</span>
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      <CallDetailsDialog call={selectedCall} open={detailsOpen} onOpenChange={setDetailsOpen} onRefresh={refetch} />
      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        selected={exportFields}
        onSelectedChange={setExportFields}
        format={exportFormat}
        onFormatChange={setExportFormat}
        onExport={handleExport}
        onCancel={() => exportAbort.current?.abort()}
        isExporting={isExporting}
        progress={exportProgress}
        callCount={totalCount}
        hasFilters={hasActiveFilters}
      />
    </div>
    {filterDrawerOpen && (
      <div
        className="fixed inset-0 z-40 bg-black/80 animate-in fade-in-0 duration-200"
        onClick={() => setFilterDrawerOpen(false)}
      />
    )}
    <CustomFilter
      config={filterConfig}
      values={filters}
      onChange={handleFilterChange}
      open={filterDrawerOpen}
      onOpenChange={setFilterDrawerOpen}
    />
    </>
  );
}
